import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { NgSelectModule } from '@ng-select/ng-select';
import { JwtModule } from '@auth0/angular-jwt';
import { ToastrModule } from 'ngx-toastr';
import { ModalModule } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { DownloadFileComponent } from "./components/downloadfile/downloadfile.component";
import { AboutComponent } from "./components/about/about.component";
import { AppComponent } from "./components/app/app.component";
import { BusinessFunctionSelectorComponent } from "./components/businessfunctionselector/businessfunctionselector.component";
import { CountryComparisonComponent } from "./components/countrycomparison/countrycomparison.component";
import { CountryComparisonItemComponent } from "./components/countrycomparison/countrycomparisonitem/countrycomparisonitem.component";
import { CountryContentComponent } from "./components/countrycontent/countrycontent.component";
import { CountryInformationComponent } from "./components/countryinformation/countryinformation.component";
import { CountryPublicationHistoryComponent } from "./components/countrypublicationhistory/countrypublicationhistory.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { DeloitteAdComponent } from "./components/deloittead/deloittead.component";
import { DeloitteDpassComponent } from "./components/deloittead/deloittedpass.component";
import { DisclaimerComponent } from "./components/disclaimer/disclaimer.component";
import { EventsComponent } from "./components/events/events.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HistoryModalComponent } from "./components/historyModal/historyModal.component";
import { HistoryModalItemComponent } from "./components/historyModal/historyModalItem.component";
import { MetadataModalComponent } from "./components/metadataModal/metadataModal.component";
import { MetadataModalItemComponent } from "./components/metadataModal/metadataModalItem.component";
import { HomeComponent } from "./components/home/home.component";
import { LoginComponent } from "./components/login/login.component";
import { MapComponent } from "./components/map/map.component";
import { NavMenuComponent } from "./components/navmenu/navmenu.component";
import { NoContentMessageComponent } from "./components/nocontentmessage/nocontentmessage.component";
import { PasswordResetRequestComponent } from "./components/passwordresetrequest/passwordresetrequest.component";
import { ResetPasswordComponent } from "./components/resetpassword/resetpassword.component";
import { SignedOutComponent } from "./components/signedout/signedout.component";
import { SplashComponent } from "./components/splash/splash.component";
import { TermsOfUseComponent } from "./components/termsofuse/termsofuse.component";
import { UnauthorizedComponent } from "./components/unauthorized/unauthorized.component";
import { GenericTermsOfUse } from "./components/generictermsofuse/generictermsofuse.component";

import { DashboardService } from "./services/dashboard.service";
import { AuditHistoryService } from "./services/audithistory.service";
import { DownloadService } from "./services/download.service";
import { RegionService } from "./services/region.service";
import { ApplicationSettingsService } from "./services/applicationsettings.service";
import { CountryComparisonService } from "./services/countrycomparison.service";
import { CountryInformationService } from "./services/countryinformation.service";
import { EventService } from "./services/event.service";
import { LatestUpdateService } from "./services/latestupdate.service";
import { BusinessFunctionService } from "./services/businessfunction.service";
import { AuthService } from "./services/auth.service";
import { AuthGuardService } from "./services/authguard.service";
import { RoutesService } from "./services/routes.service";
import { LoginService } from "./services/login.service";
import { DisclaimerService } from "./services/disclaimer.service";
import { ConfigurationService } from "./services/configuration.service";
import { AppUserService } from "./services/appuser.service";
import { ResetPasswordService } from "./services/resetpassword.service";
import { FeatureGuardService } from "./services/featureguard.service";
import { CiamUserService } from "./services/ciamuser.service";

import { DashboardResolver } from "./resolvers/dashboard.resolver";
import { RegionsResolver } from "./resolvers/regions.resolver";
import { CountryComparisonResolver } from "./resolvers/countrycomparison.resolver";
import { EventsResolver } from "./resolvers/events.resolver";
import { CountriesWithCategoryDetailResolver } from "./resolvers/countrieswithcategorydetail.resolver";
import { BusinessFunctionsResolver } from "./resolvers/businessfunctions.resolver";
import { UserBusinessFunctionsResolver } from "./resolvers/userbusinessfunctions.resolver";

import { TruncatePipe } from "./pipes/truncate.pipe";
import { EventFilterPipe } from "./pipes/eventfilter.pipe";
import { CountryCategoryFilterPipe } from "./pipes/countrycategoryfilter.pipe";

import { FeatureToggleDirective } from "./directives/featuretoggle.directive";
import { Features } from "./enums/features";
import { EmailSubscriptionModalComponent } from "./components/emailSubscriptionModal/emailSubscriptionModal.component";
import { PortalSubscriptionService } from "./services/portalsubscription.service";
import { MultipleCountryComparisionPDFModalComponent } from "./components/multipleCountryComparisionPDFModal/multipleCountryComparisionPDFModal.component";
import { MultiCountryResolver } from "./resolvers/multicountry.resolver";
import { CountriesForPublicPortalResolver } from "./resolvers/countriesforpublicportal.resolver";

declare global {
    interface Window {
        digitalData?: any;
        _satellite?: any;
    }
    // interface Navigator {
    //   msSaveBlob?: (blob: any, defaultName?: string) => boolean
    // }
}

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        // components
        AboutComponent,
        AppComponent,
        BusinessFunctionSelectorComponent,
        CountryComparisonComponent,
        CountryComparisonItemComponent,
        CountryContentComponent,
        CountryInformationComponent,
        CountryPublicationHistoryComponent,
        DashboardComponent,
        DeloitteAdComponent,
        DeloitteDpassComponent,
        DisclaimerComponent,
        DownloadFileComponent,
        EventsComponent,
        FooterComponent,
        HistoryModalComponent,
        HistoryModalItemComponent,
        MetadataModalComponent,
        MetadataModalItemComponent,
        HomeComponent,
        LoginComponent,
        MapComponent,
        NavMenuComponent,
        NoContentMessageComponent,
        PasswordResetRequestComponent,
        ResetPasswordComponent,
        SignedOutComponent,
        SplashComponent,
        TermsOfUseComponent,
        UnauthorizedComponent,
        GenericTermsOfUse,
        EmailSubscriptionModalComponent,
        // pipes
        CountryCategoryFilterPipe,
        EventFilterPipe,
        TruncatePipe,
        FeatureToggleDirective,
        MultipleCountryComparisionPDFModalComponent
    ], imports: [BrowserModule,
        FormsModule,
        NgSelectModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot(),
        ModalModule.forRoot(),
        JwtModule.forRoot({
            config: {
                tokenGetter: () => {
                    return localStorage.getItem('access_token');
                }
            }
        }),
        RouterModule.forRoot([
            {
                path: "",
                redirectTo: "splash",
                pathMatch: "full"
            },
            {
                path: "splash",
                component: SplashComponent
            },
            {
                path: "resetpassword",
                component: ResetPasswordComponent
            },
            {
                path: ":defaultroute/passwordresetrequest",
                component: PasswordResetRequestComponent
            },
            {
                path: "businessfunctionselector",
                component: BusinessFunctionSelectorComponent,
                resolve: {
                    businessFunctions: BusinessFunctionsResolver
                }
            },
            {
                path: "generictermsofuse",
                component: GenericTermsOfUse
            },
            {
                path: "deloittead",
                component: DeloitteAdComponent
            },
            {
                path: "deloittedpass",
                component: DeloitteDpassComponent
            },
            {
                path: "unauthorized",
                component: UnauthorizedComponent
            },
            {
                path: ":defaultroute",
                component: HomeComponent,
                canActivate: [AuthGuardService],
                resolve: {
                    dashboard: DashboardResolver,
                    countriesWithCategoryDetail: CountriesWithCategoryDetailResolver,
                    countriesForPublicPortal: CountriesForPublicPortalResolver
                },
                data: {
                    title: "Default Home Page",
                    nav: {
                        text: "Home",
                        navPath: ":defaultroute",
                        order: 5
                    }
                }
            },
            {
                path: ":defaultroute/logon",
                component: LoginComponent
            },
            {
                path: ":defaultroute/signedout",
                component: SignedOutComponent
            },
            {
                path: ":defaultroute/dashboard",
                component: DashboardComponent,
                canActivate: [AuthGuardService],
                resolve: {
                    dashboard: DashboardResolver,
                    regions: RegionsResolver,
                    countriesWithCategoryDetail: CountriesWithCategoryDetailResolver
                },
                data: {
                    nav: {
                        text: "Map",
                        navPath: ":defaultroute/dashboard",
                        order: 10
                    }
                }
            },
            {
                path: ":defaultroute/countryinformation/:countryCode/:categoryId",
                component: CountryInformationComponent,
                canActivate: [AuthGuardService],
                resolve: {
                    countriesWithCategoryDetail: CountriesWithCategoryDetailResolver
                }
            },
            {
                path: ":defaultroute/countryinformation/:countryCode",
                component: CountryInformationComponent,
                canActivate: [AuthGuardService],
                resolve: {
                    countriesWithCategoryDetail: CountriesWithCategoryDetailResolver
                }
            },
            {
                path: ":defaultroute/countryinformation",
                component: CountryInformationComponent,
                canActivate: [AuthGuardService],
                resolve: {
                    countriesWithCategoryDetail: CountriesWithCategoryDetailResolver,
                },
                data: {
                    nav: {
                        text: "Location summary",
                        navPath: ":defaultroute/countryinformation",
                        order: 15
                    }
                }
            },
            {
                path: ":defaultroute/countrycomparison",
                component: CountryComparisonComponent,
                canActivate: [AuthGuardService, FeatureGuardService],
                resolve: {
                    country: CountryComparisonResolver,
                    countries: CountriesWithCategoryDetailResolver,
                    multicountry: MultiCountryResolver
                },
                data: {
                    nav: {
                        text: "Location comparison",
                        navPath: ":defaultroute/countrycomparison",
                        order: 16,
                        features: [
                            Features.CountryComparison
                        ]
                    }
                }
            },
            {
                path: ":defaultroute/timeline",
                component: EventsComponent,
                canActivate: [AuthGuardService, FeatureGuardService],
                resolve: {
                    events: EventsResolver
                },
                data: {
                    nav: {
                        text: "Timeline",
                        navPath: ":defaultroute/timeline",
                        order: 20,
                        features: [
                            Features.Timeline
                        ]
                    }
                }
            }, {
                path: ":defaultroute/about",
                component: AboutComponent,
                canActivate: [AuthGuardService, FeatureGuardService],
                data: {
                    nav: {
                        text: "About",
                        navPath: ":defaultroute/about",
                        order: 25,
                        features: [
                            Features.AboutUs
                        ]
                    }
                }
            }, {
                path: ":defaultroute/termsofuse",
                component: TermsOfUseComponent,
                canActivate: [AuthGuardService]
            },
            { path: "**", redirectTo: "splash" }
        ])], providers: [
            ApplicationSettingsService,
            AppUserService,
            AuditHistoryService,
            AuthGuardService,
            AuthService,
            BusinessFunctionService,
            BusinessFunctionsResolver,
            UserBusinessFunctionsResolver,
            CiamUserService,
            ConfigurationService,
            CountriesWithCategoryDetailResolver,
            CountryComparisonResolver,
            CountryInformationService,
            CountryComparisonService,
            DashboardResolver,
            DashboardService,
            DisclaimerService,
            DownloadService,
            EventService,
            EventsResolver,
            FeatureGuardService,
            LatestUpdateService,
            LoginService,
            RegionService,
            RegionsResolver,
            ResetPasswordService,
            RoutesService,
            CookieService,
            PortalSubscriptionService,
            MultiCountryResolver,
            CountriesForPublicPortalResolver,
            provideHttpClient(withInterceptorsFromDi())
        ]
})
export class AppModule {
}